import { useMemo } from 'react';

import {
    Tooltip,
    TooltipContent,
    TooltipTrigger,
} from '@apps/web/components/ui/tooltip';
import { ANALYTICS_TIMEZONE } from '@apps/web/features/admin_agent_notes/model/constants';
import {
    buildCalendarGrid,
    type HeatmapBin,
} from '@apps/web/features/admin_agent_notes/model/heatmap_scale';
import { formatDateKey } from '@apps/web/lib/date_utils';
import { cn } from '@apps/web/lib/utils';
import { type AdminAgentNoteStatsResponse } from '@libraries/typescript/api_client';

// GitHub-style green ramp (light→dark), both themes.
const BIN_CLASSES: Record<HeatmapBin, string> = {
    0: 'bg-muted',
    1: 'bg-[oklch(0.9_0.08_155)] dark:bg-[oklch(0.38_0.07_155)]',
    2: 'bg-[oklch(0.8_0.13_155)] dark:bg-[oklch(0.5_0.11_155)]',
    3: 'bg-[oklch(0.68_0.16_155)] dark:bg-[oklch(0.62_0.15_155)]',
    4: 'bg-[oklch(0.55_0.17_155)] dark:bg-[oklch(0.75_0.17_155)]',
};

const DAY_LABELS = ['Mon', 'Wed', 'Fri'];
const DAY_LABEL_ROWS = [1, 3, 5];

function cellTooltip(cell: {
    readonly date: string;
    readonly total: number;
    readonly failed: number;
}): string {
    const date = formatDateKey(cell.date, ANALYTICS_TIMEZONE);
    const notes = `${cell.total} ${cell.total === 1 ? 'note' : 'notes'}`;
    const failed = cell.failed > 0 ? ` · ${cell.failed} failed` : '';
    return `${date} · ${notes}${failed}`;
}

/**
 * GitHub-contribution-style calendar of daily note volume: cumulative
 * across all organizations by default, or the selected organization's when
 * the org filter is active (the stats response is already org-scoped).
 */
export function NotesHeatmap({
    stats,
}: {
    readonly stats: AdminAgentNoteStatsResponse;
}) {
    const grid = useMemo(() => {
        // Always a full-year grid: pad through Dec 31 with faint future
        // cells so the calendar reads like GitHub's contribution graph.
        const startYear = stats.window_start.slice(0, 4);
        const gridEnd = `${Number(startYear) + 1}-01-01`;
        return buildCalendarGrid(
            stats.daily_counts,
            stats.window_start,
            stats.window_end,
            gridEnd,
        );
    }, [stats]);
    const sideStats = useMemo(() => {
        const realCells = grid.weeks
            .flat()
            .filter((cell) => !cell.isPadding && !cell.isFuture);
        let peak: (typeof realCells)[number] | null = null;
        let weekdayTotal = 0;
        let weekdayDays = 0;
        let weekendTotal = 0;
        let weekendDays = 0;
        for (const cell of realCells) {
            if (!peak || cell.total > peak.total) {
                peak = cell;
            }
            const day = new Date(`${cell.date}T12:00:00Z`).getUTCDay();
            if (day === 0 || day === 6) {
                weekendTotal += cell.total;
                weekendDays += 1;
            } else {
                weekdayTotal += cell.total;
                weekdayDays += 1;
            }
        }
        const perDay = (total: number, days: number) =>
            days > 0 ? total / days : 0;
        return {
            peak,
            average: perDay(
                weekdayTotal + weekendTotal,
                weekdayDays + weekendDays,
            ),
            weekdayAverage: perDay(weekdayTotal, weekdayDays),
            weekendAverage: perDay(weekendTotal, weekendDays),
        };
    }, [grid]);

    return (
        <div className="flex items-start gap-6">
            <div className="flex min-w-0 flex-1 flex-col gap-2">
                <div className="flex gap-2 overflow-x-auto pb-1">
                    <div
                        aria-hidden="true"
                        className="text-muted-foreground grid flex-none grid-rows-7 gap-1 pt-6 text-[10px]"
                    >
                        {[0, 1, 2, 3, 4, 5, 6].map((dayIndex) => (
                            <span
                                key={dayIndex}
                                className="flex h-[18px] items-center"
                            >
                                {DAY_LABEL_ROWS.includes(dayIndex)
                                    ? DAY_LABELS[
                                          DAY_LABEL_ROWS.indexOf(dayIndex)
                                      ]
                                    : ''}
                            </span>
                        ))}
                    </div>
                    <div className="flex flex-col gap-1.5">
                        <div className="flex gap-1">
                            {grid.monthLabels.map((label, weekIndex) => (
                                <span
                                    key={weekIndex}
                                    className="text-muted-foreground w-[18px] text-[10px]"
                                >
                                    {label}
                                </span>
                            ))}
                        </div>
                        <div className="flex gap-1">
                            {grid.weeks.map((week, weekIndex) => (
                                <div
                                    key={weekIndex}
                                    className="grid grid-rows-7 gap-1"
                                >
                                    {week.map((cell, dayIndex) =>
                                        cell.isPadding ? (
                                            <span
                                                key={dayIndex}
                                                className="h-[18px] w-[18px]"
                                            />
                                        ) : cell.isFuture ? (
                                            <span
                                                key={cell.date}
                                                aria-hidden="true"
                                                className="bg-muted/40 h-[18px] w-[18px] rounded-[3px]"
                                            />
                                        ) : (
                                            <Tooltip key={cell.date}>
                                                <TooltipTrigger
                                                    aria-label={cellTooltip(
                                                        cell,
                                                    )}
                                                    className={cn(
                                                        'h-[18px] w-[18px] rounded-[3px]',
                                                        'transition-transform hover:scale-110',
                                                        BIN_CLASSES[cell.bin],
                                                    )}
                                                />
                                                <TooltipContent>
                                                    {cellTooltip(cell)}
                                                </TooltipContent>
                                            </Tooltip>
                                        ),
                                    )}
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
                <div className="text-muted-foreground flex items-center gap-1.5 text-xs">
                    Less
                    {[0, 1, 2, 3, 4].map((bin) => (
                        <span
                            key={bin}
                            aria-hidden="true"
                            className={cn(
                                'h-3 w-3 rounded-[3px]',
                                BIN_CLASSES[bin as HeatmapBin],
                            )}
                        />
                    ))}
                    More
                </div>
            </div>
            <div className="border-border flex flex-none flex-col gap-4 border-l pl-6">
                <div className="flex flex-col gap-0.5">
                    <span className="text-muted-foreground text-xs">
                        Peak day
                    </span>
                    <span className="text-lg font-semibold tabular-nums">
                        {sideStats.peak ? `${sideStats.peak.total} notes` : '—'}
                    </span>
                    {sideStats.peak && sideStats.peak.total > 0 && (
                        <span className="text-muted-foreground text-xs">
                            {formatDateKey(
                                sideStats.peak.date,
                                ANALYTICS_TIMEZONE,
                            )}
                        </span>
                    )}
                </div>
                <div className="flex flex-col gap-0.5">
                    <span className="text-muted-foreground text-xs">
                        Average per day
                    </span>
                    <span className="text-lg font-semibold tabular-nums">
                        {sideStats.average >= 10
                            ? sideStats.average.toFixed(0)
                            : sideStats.average.toFixed(1)}
                    </span>
                </div>
                <div className="flex flex-col gap-0.5">
                    <span className="text-muted-foreground text-xs">
                        Weekday average
                    </span>
                    <span className="text-lg font-semibold tabular-nums">
                        {sideStats.weekdayAverage >= 10
                            ? sideStats.weekdayAverage.toFixed(0)
                            : sideStats.weekdayAverage.toFixed(1)}{' '}
                        per day
                    </span>
                </div>
                <div className="flex flex-col gap-0.5">
                    <span className="text-muted-foreground text-xs">
                        Weekend average
                    </span>
                    <span className="text-lg font-semibold tabular-nums">
                        {sideStats.weekendAverage >= 10
                            ? sideStats.weekendAverage.toFixed(0)
                            : sideStats.weekendAverage.toFixed(1)}{' '}
                        per day
                    </span>
                </div>
            </div>
        </div>
    );
}
